import React, { useState, useEffect, useRef, useCallback } from "react"
import { TAgetStateStats } from "../../../services/applicationAPI"
import { TAgetBrandFromIds, TAgetBrandCampaigns } from "../../../services/brandAPI"
import * as XLSX from "xlsx"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDispatch } from "react-redux"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import ApplicationSankeyDiagram from "./components/ApplicationSankeyDiagram"
import StatsHeader from "./components/StatsHeader"
import StatsSummaryCards from "./components/StatsSummaryCards"
import StatsComparisonInfo from "./components/StatsComparisonInfo"
import StatsViewModeSelector from "./components/StatsViewModeSelector"
import DailyStatsTable from "./components/DailyStatsTable"
import WeeklyStatsTable from "./components/WeeklyStatsTable"
import MonthlyStatsTable from "./components/MonthlyStatsTable"
import BrandDetailsModal from "./components/BrandDetailsModal"
import TransitionStatsCards from "./components/TransitionStatsCards"
import { DayStats, WeekStats, MonthStats, BrandStat } from "./types"
import { getErrorMessage, getErrorStatus, isAbortError } from "../../../types/error"

interface StateStat {
  _id: string
  count: number
}

type StateStatsData = DayStats[]

interface UserStatsSum {
  calculatedAt: string
  dateRange: {
    start: string
    end: string
  }
  fastestTransitions: {
    transition: string
    average: number
    totalCount: number
  }[]
  slowestTransitions: {
    transition: string
    average: number
    totalCount: number
  }[]
  stateTransitionStats: Record<
    string,
    {
      average: number
      minimum: number
      maximum: number
      totalCount: number
      totalDuration: number
    }
  >
  totalApplications: number
  totalApplicationsAccepted: number
  totalApplicationsDone: number
  totalApplicationsRejected: number
  totalApplicationsToApproveRatio: number
  totalApplicationsToDoneRatio: number
  totalApplicationsToRejectRatio: number
  totalUsers: number
}

const ApplicationStats: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const abortControllerRef = useRef<AbortController | null>(null)
  const [statsData, setStatsData] = useState<StateStatsData | null>(null)
  const [userStatsSum, setUserStatsSum] = useState<UserStatsSum | null>(null)

  const getDefaultDays = () => {
    const daysParam = searchParams.get("days")
    return daysParam ? parseInt(daysParam, 10) : 90
  }

  const getDefaultStartDate = () => {
    const startParam = searchParams.get("startDate")
    if (startParam) {
      return startParam
    }
    const days = getDefaultDays()
    return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  }

  const getDefaultEndDate = () => {
    const endParam = searchParams.get("endDate")
    return endParam || new Date().toISOString().split("T")[0]
  }

  const getDefaultViewMode = (): "daily" | "weekly" | "monthly" => {
    const modeParam = searchParams.get("viewMode")
    if (modeParam === "daily" || modeParam === "weekly" || modeParam === "monthly") {
      return modeParam
    }
    return "weekly"
  }

  const [selectedDays, setSelectedDays] = useState<number>(getDefaultDays())
  const [startDate, setStartDate] = useState<string>(getDefaultStartDate())
  const [endDate, setEndDate] = useState<string>(getDefaultEndDate())
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [viewMode, setViewMode] = useState<"daily" | "weekly" | "monthly">(getDefaultViewMode())
  const [viewModeLoading, setViewModeLoading] = useState<boolean>(false)
  const [showBrandModal, setShowBrandModal] = useState(false)
  const [selectedTransition, setSelectedTransition] = useState<{
    from: string
    to: string
    brands: BrandStat[]
    date: string
  } | null>(null)

  const fetchStats = useCallback(
    async (start: string, end: string) => {
      setError(null)
      setLoading(true)

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const currentAbortController = new AbortController()
      abortControllerRef.current = currentAbortController

      try {
        const data = await TAgetStateStats(start, end, currentAbortController.signal)
        setStatsData(data.stats)
        setUserStatsSum(data.userStatsSum)
      } catch (err: unknown) {
        // Abort edildiyse, yeni istek başlatılmış olabilir, loading state'ini değiştirme
        if (isAbortError(err)) {
          return
        }

        if (getErrorStatus(err) === 401) {
          navigate("/login")
          setLoading(false)
          return
        }
        setError(getErrorMessage(err) || "İstatistikler alınırken bir hata oluştu.")
        setLoading(false)
      } finally {
        // Sadece bu istek hala aktifse loading'i false yap
        if (abortControllerRef.current === currentAbortController) {
          setLoading(false)
        }
      }
    },
    [navigate],
  )

  const getYmd = (iso: string) => iso.slice(0, 10)

  const formatYmdTR = (ymd: string) => {
    const [Y, M, D] = ymd.split("-")
    return `${D}.${M}.${Y.slice(2)}`
  }

  const parseYmdToUTC = (ymd: string) => {
    const [Y, M, D] = ymd.split("-").map(Number)
    return new Date(Date.UTC(Y, M - 1, D))
  }

  const lastIdx = statsData ? statsData.length - 1 : -1
  const currentStats: StateStat[] =
    lastIdx >= 0
      ? (statsData![lastIdx].stateCounts || []).map((sc) => ({ _id: sc.state, count: sc.count }))
      : []
  const previousStats: StateStat[] =
    lastIdx > 0
      ? (statsData![lastIdx - 1].stateCounts || []).map((sc) => ({
        _id: sc.state,
        count: sc.count,
      }))
      : []

  // State değiştiğinde URL'i güncelle
  useEffect(() => {
    const params = new URLSearchParams()
    params.set("days", selectedDays.toString())
    params.set("startDate", startDate)
    params.set("endDate", endDate)
    params.set("viewMode", viewMode)
    setSearchParams(params, { replace: true })
  }, [selectedDays, startDate, endDate, viewMode, setSearchParams])

  useEffect(() => {
    dispatch(setPageTitle("Application State Statistics"))
    fetchStats(startDate, endDate)
  }, [dispatch, fetchStats, startDate, endDate])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const snapshotDate: string = React.useMemo(() => {
    if (!statsData || statsData.length === 0) {
      return ""
    }
    return statsData[statsData.length - 1].date
  }, [statsData])

  const allStatesSet = React.useMemo(() => {
    const set = new Set<string>()
    statsData?.forEach((day) => {
      day.stateCounts?.forEach((stat) => {
        if (stat.state !== null && stat.state !== undefined) {
          set.add(stat.state)
        }
      })
    })
    return set
  }, [statsData])

  const handleDateRangeChange = async () => {
    await fetchStats(startDate, endDate)
  }

  const getStateDisplayName = (state: string) => {
    const stateNames: { [key: string]: string } = {
      pending: "Beklemede",
      approved: "Onaylandı",
      rejected: "Reddedildi",
      completed: "Tamamlandı",
      in_progress: "Devam Ediyor",
      cancelled: "İptal Edildi",
      draft: "Taslak",
      submitted: "Gönderildi",
      under_review: "İncelemede",
      accepted: "Kabul Edildi",
      declined: "Reddedildi",
    }
    return stateNames[state] || state
  }

  const getStateColor = (state: string) => {
    const colors: { [key: string]: string } = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      completed: "bg-blue-100 text-blue-800",
      in_progress: "bg-purple-100 text-purple-800",
      cancelled: "bg-gray-100 text-gray-800",
      draft: "bg-slate-100 text-slate-800",
      submitted: "bg-orange-100 text-orange-800",
      under_review: "bg-indigo-100 text-indigo-800",
      accepted: "bg-emerald-100 text-emerald-800",
      declined: "bg-rose-100 text-rose-800",
    }
    return colors[state] || "bg-gray-100 text-gray-800"
  }

  const prepareDataForDownload = (): any[][] => {
    if (!statsData) {
      return []
    }

    const downloadData: any[][] = []
    const headers = ["Durum"]

    statsData.forEach((day) => {
      const dateStr = formatYmdTR(getYmd(day.date))

      headers.push(
        `${dateStr} - Toplam Başvuru`,
        `${dateStr} - State'e Geçen`,
        `${dateStr} - State'den Çıkan`,
        `${dateStr} - Değişim`,
      )
    })

    downloadData.push(headers)
    const allStates = Array.from(allStatesSet).sort()

    const userSideStates = [
      "invite_new",
      "waiting_address",
      "address_rejected",
      "waiting_content",
      "content_rejected",
      "ugc_rejected",
      "content_approved",
    ]
    const companySideStates = [
      "invite_success",
      "first_application",
      "address_to_approve",
      "content_to_share",
      "content_shared",
      "ugc_content",
    ]

    const userSideStatesInData = userSideStates.filter((state) => allStates.includes(state))
    if (userSideStatesInData.length > 0) {
      downloadData.push(["Kullanıcı Tarafı"])
      userSideStatesInData.forEach((state) => {
        const row = [getStateDisplayName(state)]
        statsData.forEach((day) => {
          const endStat = (day.stateCounts || []).find((s) => s.state === state)
          const incomingCount = (day.stateTransitions || []).reduce((total, t) => {
            if (t.to === state) {
              return total + t.count
            }
            return total
          }, 0)
          const outgoingCount = (day.stateTransitions || []).reduce((total, t) => {
            if (t.from === state) {
              return total + t.count
            }
            return total
          }, 0)
          const change = incomingCount - outgoingCount

          row.push(
            endStat ? endStat.count.toString() : "0",
            incomingCount.toString(),
            outgoingCount.toString(),
            change.toString(),
          )
        })
        downloadData.push(row)
      })
    }

    const companySideStatesInData = companySideStates.filter(
      (state) => allStates.includes(state) && !userSideStates.includes(state),
    )
    if (companySideStatesInData.length > 0) {
      downloadData.push(["Firma Tarafı"])
      companySideStatesInData.forEach((state) => {
        const row = [getStateDisplayName(state)]
        statsData.forEach((day) => {
          const endStat = (day.stateCounts || []).find((s) => s.state === state)
          const incomingCount = (day.stateTransitions || []).reduce((total, t) => {
            if (t.to === state) {
              return total + t.count
            }
            return total
          }, 0)
          const outgoingCount = (day.stateTransitions || []).reduce((total, t) => {
            if (t.from === state) {
              return total + t.count
            }
            return total
          }, 0)
          const change = incomingCount - outgoingCount

          row.push(
            endStat ? endStat.count.toString() : "0",
            incomingCount.toString(),
            outgoingCount.toString(),
            change.toString(),
          )
        })
        downloadData.push(row)
      })
    }

    const otherStates = allStates.filter(
      (state) => !userSideStates.includes(state) && !companySideStates.includes(state),
    )
    if (otherStates.length > 0) {
      downloadData.push(["Diğer"])
      otherStates.forEach((state) => {
        const row = [getStateDisplayName(state)]
        statsData.forEach((day) => {
          const endStat = (day.stateCounts || []).find((s) => s.state === state)
          const incomingCount = (day.stateTransitions || []).reduce((total, t) => {
            if (t.to === state) {
              return total + t.count
            }
            return total
          }, 0)
          const outgoingCount = (day.stateTransitions || []).reduce((total, t) => {
            if (t.from === state) {
              return total + t.count
            }
            return total
          }, 0)
          const change = incomingCount - outgoingCount

          row.push(
            endStat ? endStat.count.toString() : "0",
            incomingCount.toString(),
            outgoingCount.toString(),
            change.toString(),
          )
        })
        downloadData.push(row)
      })
    }

    return downloadData
  }

  const downloadCSV = () => {
    const data = prepareDataForDownload()
    if (data.length === 0) {
      return
    }

    const csvContent = data.map((row) => row.join(",")).join("\n")

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute(
      "download",
      `başvuru_istatistikleri_${viewMode}_${new Date().toISOString().split("T")[0]}.csv`,
    )
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new()

    const dailyData = prepareDataForDownload()

    if (dailyData.length > 0) {
      const dailyWorksheet = XLSX.utils.aoa_to_sheet(dailyData)

      const dailyMaxWidths = dailyData.reduce((widths, row) => {
        row.forEach((cell, index) => {
          const cellLength = String(cell).length
          widths[index] = Math.max(widths[index] || 0, cellLength)
        })
        return widths
      }, {} as { [key: number]: number })

      const dailyColumnWidths = Object.keys(dailyMaxWidths).map((key) => ({
        wch: Math.min(Math.max(dailyMaxWidths[parseInt(key)] + 2, 10), 50),
      }))
      dailyWorksheet["!cols"] = dailyColumnWidths

      XLSX.utils.book_append_sheet(workbook, dailyWorksheet, "Günlük İstatistikler")
    }

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })

    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute(
      "download",
      `başvuru_istatistikleri_${new Date().toISOString().split("T")[0]}.xlsx`,
    )
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const fetchBrandNames = async (brands: BrandStat[]) => {
    try {
      const brandIds = brands.map((brand) => brand.brand)
      const brandDataArray = await TAgetBrandFromIds(brandIds)

      const brandsWithData = await Promise.all(
        brands.map(async (brand) => {
          const matchingBrand = brandDataArray.find((data: any) => data._id === brand.brand)

          let campaignDetails: { id: string; name: string }[] = []
          if (brand.campaigns && brand.campaigns.length > 0) {
            try {
              const campaigns = await TAgetBrandCampaigns(brand.brand)
              campaignDetails = brand.campaigns
                .map((cStat) => {
                  const id = cStat.campaign
                  const c = campaigns.find((camp: any) => camp._id === id)
                  return c ? { id: c._id, name: c.name } : null
                })
                .filter((c): c is { id: string; name: string } => c !== null)
            } catch (e) {
              campaignDetails = brand.campaigns.map((cStat) => ({
                id: cStat.campaign,
                name: cStat.campaign,
              }))
            }
          }

          return {
            ...brand,
            brandName: matchingBrand ? matchingBrand.brand_name : brand.brand,
            campaignDetails,
          }
        }),
      )
      return brandsWithData
    } catch (error) {
      return brands.map((brand) => ({ ...brand, brandName: brand.brand }))
    }
  }

  const handleTotalClickDaily = async (state: string, day: DayStats) => {
    const endStat = (day.stateCounts || []).find((s) => s.state === state)
    const brands = endStat?.brands || []
    if (!brands.length) {
      return
    }

    const sortedBrands = brands.sort((a, b) => b.count - a.count)
    const brandsWithNames = await fetchBrandNames(sortedBrands)
    setSelectedTransition({
      from: "Toplam Başvuru",
      to: state,
      brands: brandsWithNames,
      date: `${getYmd(day.date)} - ${getYmd(day.date)}`,
    })
    setShowBrandModal(true)
  }

  const handleTotalClickWeekly = async (state: string, week: WeekStats) => {
    const brandAggregates = new Map<
      string,
      { sum: number; days: number; campaigns: Map<string, number> }
    >()

    week.days.forEach((day: DayStats) => {
      const endStat = (day.stateCounts || []).find((s) => s.state === state)
      if (endStat?.brands) {
        endStat.brands.forEach((brand: BrandStat) => {
          const agg = brandAggregates.get(brand.brand) || {
            sum: 0,
            days: 0,
            campaigns: new Map<string, number>(),
          }
          agg.sum += brand.count
          agg.days += 1
          if (brand.campaigns) {
            brand.campaigns.forEach((c) => {
              const currentCount = agg.campaigns.get(c.campaign) || 0
              agg.campaigns.set(c.campaign, currentCount + c.count)
            })
          }
          brandAggregates.set(brand.brand, agg)
        })
      }
    })

    if (brandAggregates.size === 0) {
      return
    }

    const brandsArray: BrandStat[] = Array.from(brandAggregates.entries()).map(([brand, agg]) => ({
      brand,
      count: Math.round(agg.sum / Math.max(agg.days, 1)),
      campaigns: Array.from(agg.campaigns.entries()).map(([campaign, count]) => ({
        campaign,
        count: Math.round(count / Math.max(agg.days, 1)),
      })),
    }))

    const sortedBrands = brandsArray.sort((a, b) => b.count - a.count)
    const brandsWithNames = await fetchBrandNames(sortedBrands)
    setSelectedTransition({
      from: "Toplam Başvuru",
      to: state,
      brands: brandsWithNames,
      date: `${formatYmdTR(week.startDate)} - ${formatYmdTR(week.endDate)}`,
    })
    setShowBrandModal(true)
  }

  const handleTotalClickMonthly = async (state: string, month: MonthStats) => {
    const brandAggregates = new Map<
      string,
      { sum: number; days: number; campaigns: Map<string, number> }
    >()

    month.days.forEach((day: DayStats) => {
      const endStat = (day.stateCounts || []).find((s) => s.state === state)
      if (endStat?.brands) {
        endStat.brands.forEach((brand: BrandStat) => {
          const agg = brandAggregates.get(brand.brand) || {
            sum: 0,
            days: 0,
            campaigns: new Map<string, number>(),
          }
          agg.sum += brand.count
          agg.days += 1
          if (brand.campaigns) {
            brand.campaigns.forEach((c) => {
              const currentCount = agg.campaigns.get(c.campaign) || 0
              agg.campaigns.set(c.campaign, currentCount + c.count)
            })
          }
          brandAggregates.set(brand.brand, agg)
        })
      }
    })

    if (brandAggregates.size === 0) {
      return
    }

    const brandsArray: BrandStat[] = Array.from(brandAggregates.entries()).map(([brand, agg]) => ({
      brand,
      count: Math.round(agg.sum / Math.max(agg.days, 1)),
      campaigns: Array.from(agg.campaigns.entries()).map(([campaign, count]) => ({
        campaign,
        count: Math.round(count / Math.max(agg.days, 1)),
      })),
    }))

    const sortedBrands = brandsArray.sort((a, b) => b.count - a.count)
    const brandsWithNames = await fetchBrandNames(sortedBrands)
    setSelectedTransition({
      from: "Toplam Başvuru",
      to: state,
      brands: brandsWithNames,
      date: `${formatYmdTR(month.startDate)} - ${formatYmdTR(month.endDate)}`,
    })
    setShowBrandModal(true)
  }

  const handleTransitionClick = async (
    type: "incoming" | "outgoing",
    state: string,
    date: string,
  ) => {
    const dayData = statsData?.find((day) => getYmd(day.date) === getYmd(date))
    if (dayData) {
      let allBrands: BrandStat[] = []

      if (type === "incoming") {
        const incomingTransitions = dayData.stateTransitions.filter((t) => t.to === state)

        allBrands = incomingTransitions.reduce((acc, transition) => {
          if (transition.brands && transition.brands.length > 0) {
            transition.brands.forEach((brand) => {
              const existingBrand = acc.find((b) => b.brand === brand.brand)
              if (existingBrand) {
                existingBrand.count += brand.count
                if (brand.campaigns) {
                  brand.campaigns.forEach((c) => {
                    const existingCampaign = existingBrand.campaigns.find(
                      (ec) => ec.campaign === c.campaign,
                    )
                    if (existingCampaign) {
                      existingCampaign.count += c.count
                    } else {
                      existingBrand.campaigns.push({ ...c })
                    }
                  })
                }
              } else {
                acc.push({
                  brand: brand.brand,
                  count: brand.count,
                  campaigns: brand.campaigns ? [...brand.campaigns] : [],
                })
              }
            })
          }
          return acc
        }, [] as BrandStat[])
      } else if (type === "outgoing") {
        const outgoingTransitions = dayData.stateTransitions.filter((t) => t.from === state)

        allBrands = outgoingTransitions.reduce((acc, transition) => {
          if (transition.brands && transition.brands.length > 0) {
            transition.brands.forEach((brand) => {
              const existingBrand = acc.find((b) => b.brand === brand.brand)
              if (existingBrand) {
                existingBrand.count += brand.count
                if (brand.campaigns) {
                  brand.campaigns.forEach((c) => {
                    const existingCampaign = existingBrand.campaigns.find(
                      (ec) => ec.campaign === c.campaign,
                    )
                    if (existingCampaign) {
                      existingCampaign.count += c.count
                    } else {
                      existingBrand.campaigns.push({ ...c })
                    }
                  })
                }
              } else {
                acc.push({
                  brand: brand.brand,
                  count: brand.count,
                  campaigns: brand.campaigns ? [...brand.campaigns] : [],
                })
              }
            })
          }
          return acc
        }, [] as BrandStat[])
      }

      if (allBrands.length > 0) {
        const sortedBrands = allBrands.sort((a, b) => b.count - a.count)

        const brandsWithNames = await fetchBrandNames(sortedBrands)

        setSelectedTransition({
          from: type === "incoming" ? "Tüm Gelen Geçişler" : "Tüm Giden Geçişler",
          to: state,
          brands: brandsWithNames,
          date: `${formatYmdTR(getYmd(date))} - ${formatYmdTR(getYmd(date))}`,
        })
        setShowBrandModal(true)
      }
    }
  }

  const handleTransitionClickWeekly = async (
    type: "incoming" | "outgoing",
    state: string,
    week: WeekStats,
  ) => {
    let allBrands: BrandStat[] = []

    if (type === "incoming") {
      week.days.forEach((day: DayStats) => {
        const incomingTransitions = day.stateTransitions.filter((t) => t.to === state)
        incomingTransitions.forEach((transition) => {
          if (transition.brands && transition.brands.length > 0) {
            transition.brands.forEach((brand) => {
              const existingBrand = allBrands.find((b) => b.brand === brand.brand)
              if (existingBrand) {
                existingBrand.count += brand.count
                if (brand.campaigns) {
                  brand.campaigns.forEach((c) => {
                    const existingCampaign = existingBrand.campaigns.find(
                      (ec) => ec.campaign === c.campaign,
                    )
                    if (existingCampaign) {
                      existingCampaign.count += c.count
                    } else {
                      existingBrand.campaigns.push({ ...c })
                    }
                  })
                }
              } else {
                allBrands.push({
                  brand: brand.brand,
                  count: brand.count,
                  campaigns: brand.campaigns ? [...brand.campaigns] : [],
                })
              }
            })
          }
        })
      })
    } else if (type === "outgoing") {
      week.days.forEach((day: DayStats) => {
        const outgoingTransitions = day.stateTransitions.filter((t) => t.from === state)
        outgoingTransitions.forEach((transition) => {
          if (transition.brands && transition.brands.length > 0) {
            transition.brands.forEach((brand) => {
              const existingBrand = allBrands.find((b) => b.brand === brand.brand)
              if (existingBrand) {
                existingBrand.count += brand.count
                if (brand.campaigns) {
                  brand.campaigns.forEach((c) => {
                    const existingCampaign = existingBrand.campaigns.find(
                      (ec) => ec.campaign === c.campaign,
                    )
                    if (existingCampaign) {
                      existingCampaign.count += c.count
                    } else {
                      existingBrand.campaigns.push({ ...c })
                    }
                  })
                }
              } else {
                allBrands.push({
                  brand: brand.brand,
                  count: brand.count,
                  campaigns: brand.campaigns ? [...brand.campaigns] : [],
                })
              }
            })
          }
        })
      })
    }

    if (allBrands.length > 0) {
      const sortedBrands = allBrands.sort((a, b) => b.count - a.count)
      const brandsWithNames = await fetchBrandNames(sortedBrands)

      setSelectedTransition({
        from: type === "incoming" ? "Tüm Gelen Geçişler" : "Tüm Giden Geçişler",
        to: state,
        brands: brandsWithNames,
        date: `${formatYmdTR(week.startDate)} - ${formatYmdTR(week.endDate)}`,
      })
      setShowBrandModal(true)
    }
  }

  const handleTransitionClickMonthly = async (
    type: "incoming" | "outgoing",
    state: string,
    month: MonthStats,
  ) => {
    let allBrands: BrandStat[] = []

    if (type === "incoming") {
      month.days.forEach((day: DayStats) => {
        const incomingTransitions = day.stateTransitions.filter((t) => t.to === state)
        incomingTransitions.forEach((transition) => {
          if (transition.brands && transition.brands.length > 0) {
            transition.brands.forEach((brand) => {
              const existingBrand = allBrands.find((b) => b.brand === brand.brand)
              if (existingBrand) {
                existingBrand.count += brand.count
                if (brand.campaigns) {
                  brand.campaigns.forEach((c) => {
                    const existingCampaign = existingBrand.campaigns.find(
                      (ec) => ec.campaign === c.campaign,
                    )
                    if (existingCampaign) {
                      existingCampaign.count += c.count
                    } else {
                      existingBrand.campaigns.push({ ...c })
                    }
                  })
                }
              } else {
                allBrands.push({
                  brand: brand.brand,
                  count: brand.count,
                  campaigns: brand.campaigns ? [...brand.campaigns] : [],
                })
              }
            })
          }
        })
      })
    } else if (type === "outgoing") {
      month.days.forEach((day: DayStats) => {
        const outgoingTransitions = day.stateTransitions.filter((t) => t.from === state)
        outgoingTransitions.forEach((transition) => {
          if (transition.brands && transition.brands.length > 0) {
            transition.brands.forEach((brand) => {
              const existingBrand = allBrands.find((b) => b.brand === brand.brand)
              if (existingBrand) {
                existingBrand.count += brand.count
                if (brand.campaigns) {
                  brand.campaigns.forEach((c) => {
                    const existingCampaign = existingBrand.campaigns.find(
                      (ec) => ec.campaign === c.campaign,
                    )
                    if (existingCampaign) {
                      existingCampaign.count += c.count
                    } else {
                      existingBrand.campaigns.push({ ...c })
                    }
                  })
                }
              } else {
                allBrands.push({
                  brand: brand.brand,
                  count: brand.count,
                  campaigns: brand.campaigns ? [...brand.campaigns] : [],
                })
              }
            })
          }
        })
      })
    }

    if (allBrands.length > 0) {
      const sortedBrands = allBrands.sort((a, b) => b.count - a.count)
      const brandsWithNames = await fetchBrandNames(sortedBrands)

      setSelectedTransition({
        from: type === "incoming" ? "Tüm Gelen Geçişler" : "Tüm Giden Geçişler",
        to: state,
        brands: brandsWithNames,
        date: `${formatYmdTR(month.startDate)} - ${formatYmdTR(month.endDate)}`,
      })
      setShowBrandModal(true)
    }
  }

  const closeBrandModal = () => {
    setShowBrandModal(false)
    setSelectedTransition(null)
  }

  const handleViewModeChange = async (mode: "daily" | "weekly" | "monthly") => {
    setViewModeLoading(true)
    await new Promise((resolve) => setTimeout(resolve, 300))
    setViewMode(mode)
    setViewModeLoading(false)
  }

  // Process daily data into weekly aggregates
  const processWeeklyData = (dailyData: DayStats[]): WeekStats[] => {
    const weeklyMap = new Map<string, WeekStats>()
    const weeklyMeans = new Map<string, Map<string, { sum: number; days: number }>>()
    dailyData.forEach((day) => {
      const date = parseYmdToUTC(getYmd(day.date))
      const year = date.getFullYear()
      const weekNumber = getWeekNumber(date)
      const weekKey = `${year}-${weekNumber}`

      if (!weeklyMap.has(weekKey)) {
        weeklyMap.set(weekKey, {
          weekNumber,
          year,
          startDate: getYmd(day.date),
          endDate: getYmd(day.date),
          stats: [],
          transitions: new Map(),
          weeks: [],
          days: [],
        })
        weeklyMeans.set(weekKey, new Map())
      }

      const week = weeklyMap.get(weekKey)!
      week.days.push(day)

      const ymd = getYmd(day.date)
      if (ymd < week.startDate) {
        week.startDate = ymd
      }
      if (ymd > week.endDate) {
        week.endDate = ymd
      }

      // Aggregate state counts
      const means = weeklyMeans.get(weekKey)!
      day.stateCounts?.forEach((stateCount) => {
        const m = means.get(stateCount.state) || { sum: 0, days: 0 }
        m.sum += stateCount.count
        m.days += 1
        means.set(stateCount.state, m)
      })

      // Aggregate transitions
      day.stateTransitions?.forEach((transition) => {
        const state = transition.to
        const existing = week.transitions.get(state) || { incoming: 0, outgoing: 0 }
        existing.incoming += transition.count
        week.transitions.set(state, existing)

        const fromState = transition.from
        const fromExisting = week.transitions.get(fromState) || { incoming: 0, outgoing: 0 }
        fromExisting.outgoing += transition.count
        week.transitions.set(fromState, fromExisting)
      })
    })

    const result = Array.from(weeklyMap.values()).sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year
      }
      return a.weekNumber - b.weekNumber
    })

    result.forEach((week) => {
      const means = weeklyMeans.get(`${week.year}-${week.weekNumber}`)
      if (!means) {
        return
      }
      week.stats = Array.from(means.entries()).map(([state, v]) => ({
        _id: state,
        count: v.days > 0 ? Math.round(v.sum / v.days) : 0,
      }))
    })
    return result
  }

  // Process daily data into monthly aggregates
  const processMonthlyData = (dailyData: DayStats[]): MonthStats[] => {
    const monthlyMap = new Map<string, MonthStats>()
    const monthlyMeans = new Map<string, Map<string, { sum: number; days: number }>>()

    dailyData.forEach((day) => {
      const date = parseYmdToUTC(getYmd(day.date))
      const year = date.getFullYear()
      const monthNumber = date.getMonth() + 1
      const monthKey = `${year}-${monthNumber}`

      if (!monthlyMap.has(monthKey)) {
        monthlyMap.set(monthKey, {
          monthNumber,
          year,
          startDate: getYmd(day.date),
          endDate: getYmd(day.date),
          stats: [],
          transitions: new Map(),
          weeks: [],
          days: [],
        })
        monthlyMeans.set(monthKey, new Map())
      }

      const month = monthlyMap.get(monthKey)!
      month.days.push(day)

      const ymd = getYmd(day.date)
      if (ymd < month.startDate) {
        month.startDate = ymd
      }
      if (ymd > month.endDate) {
        month.endDate = ymd
      }

      const means = monthlyMeans.get(monthKey)!
      day.stateCounts?.forEach((stateCount) => {
        const m = means.get(stateCount.state) || { sum: 0, days: 0 }
        m.sum += stateCount.count
        m.days += 1
        means.set(stateCount.state, m)
      })

      day.stateTransitions?.forEach((transition) => {
        const toState = transition.to
        const toAgg = month.transitions.get(toState) || { incoming: 0, outgoing: 0 }
        toAgg.incoming += transition.count
        month.transitions.set(toState, toAgg)

        const fromState = transition.from
        const fromAgg = month.transitions.get(fromState) || { incoming: 0, outgoing: 0 }
        fromAgg.outgoing += transition.count
        month.transitions.set(fromState, fromAgg)
      })
    })

    const result = Array.from(monthlyMap.values()).sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year
      }
      return a.monthNumber - b.monthNumber
    })

    result.forEach((month) => {
      const means = monthlyMeans.get(`${month.year}-${month.monthNumber}`)
      if (!means) {
        return
      }
      month.stats = Array.from(means.entries()).map(([state, v]) => ({
        _id: state,
        count: v.days > 0 ? Math.round(v.sum / v.days) : 0,
      }))
    })

    return result
  }

  // Helper function to get week number
  const getWeekNumber = (dateUTC: Date) => {
    const yStart = new Date(Date.UTC(dateUTC.getUTCFullYear(), 0, 1))
    const pastDays = Math.floor((+dateUTC - +yStart) / 86400000)
    return Math.ceil((pastDays + yStart.getUTCDay() + 1) / 7)
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            onClick={async () => await fetchStats(startDate, endDate)}
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="flex flex-col items-center space-y-6">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-blue-600"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="animate-pulse">
                <span className="text-3xl">📊</span>
              </div>
            </div>
          </div>
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">İstatistikler Yükleniyor</h3>
            <p className="text-gray-600">Veriler hazırlanıyor, lütfen bekleyin...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <StatsHeader
        selectedDays={selectedDays}
        startDate={startDate}
        endDate={endDate}
        loading={loading}
        statsData={statsData}
        setSelectedDays={setSelectedDays}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onDateRangeChange={handleDateRangeChange}
        onRefresh={async () => await fetchStats(startDate, endDate)}
        onDownloadCSV={downloadCSV}
        onDownloadExcel={downloadExcel}
      />

      {statsData && (
        <>
          <StatsComparisonInfo
            snapshotDate={snapshotDate}
            statsDataLength={statsData.length}
            startDate={startDate}
            endDate={endDate}
          />

          <StatsSummaryCards currentStats={currentStats} previousStats={previousStats} />

          <StatsViewModeSelector viewMode={viewMode} onViewModeChange={handleViewModeChange} />

          {viewModeLoading ? (
            <div className="flex items-center justify-center py-20 bg-white rounded-xl shadow-lg border border-gray-100">
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="animate-pulse">
                      <span className="text-xl">📊</span>
                    </div>
                  </div>
                </div>
                <span className="text-base font-medium text-gray-700">
                  Görünüm güncelleniyor...
                </span>
              </div>
            </div>
          ) : (
            <>
              {viewMode === "daily" && (
                <DailyStatsTable
                  statsData={statsData}
                  allStatesSet={allStatesSet}
                  onTotalClick={handleTotalClickDaily}
                  onTransitionClick={handleTransitionClick}
                  getStateDisplayName={getStateDisplayName}
                  getStateColor={getStateColor}
                />
              )}

              {viewMode === "weekly" && (
                <WeeklyStatsTable
                  statsData={processWeeklyData(statsData)}
                  allStatesSet={allStatesSet}
                  onTotalClick={handleTotalClickWeekly}
                  onTransitionClick={handleTransitionClickWeekly}
                  getStateDisplayName={getStateDisplayName}
                  getStateColor={getStateColor}
                />
              )}

              {viewMode === "monthly" && (
                <MonthlyStatsTable
                  statsData={processMonthlyData(statsData)}
                  allStatesSet={allStatesSet}
                  onTotalClick={handleTotalClickMonthly}
                  onTransitionClick={handleTransitionClickMonthly}
                  getStateDisplayName={getStateDisplayName}
                  getStateColor={getStateColor}
                />
              )}
            </>
          )}

          {/* Sankey Diagram */}
          {userStatsSum && userStatsSum.stateTransitionStats && (
            <div className="mb-6">
              <ApplicationSankeyDiagram
                stateTransitionStats={userStatsSum.stateTransitionStats}
                width={1200}
                height={600}
              />
            </div>
          )}

          {/* Transition Statistics Cards */}
          {userStatsSum && (
            <TransitionStatsCards
              fastestTransitions={userStatsSum.fastestTransitions}
              slowestTransitions={userStatsSum.slowestTransitions}
              getStateDisplayName={getStateDisplayName}
            />
          )}

          <BrandDetailsModal
            isOpen={showBrandModal}
            selectedTransition={selectedTransition}
            onClose={closeBrandModal}
            getStateDisplayName={getStateDisplayName}
          />
        </>
      )}
    </div>
  )
}

export default ApplicationStats
